import * as React from 'react';
import * as ToastPrimitive from '@radix-ui/react-toast';
import { X } from 'lucide-react';

import { cn } from 'lib/utils';
import { toastVariants, toastTitleVariants, toastDescriptionVariants } from './toast';
import { useToast } from './use-toast';

export const Toaster: React.FC<{ placement?: 'overlay' | 'inline' }> = ({ placement = 'overlay' }) => {
  const { toasts, removeToast } = useToast();
  const EXIT_DURATION = 150;
  const PERSISTENT_DURATION_MS = 24 * 60 * 60 * 1000;

  // The full-screen meeting shell reserves this space instead of placing
  // persistent notices over Join, admission or call controls.
  if (placement === 'inline' && toasts.length === 0) return null;

  return (
    <div className={placement === 'inline' ? 'shrink-0' : undefined}>
    <ToastPrimitive.Provider swipeDirection="right">
      {toasts.map((toast) => (
        <ToastPrimitive.Root
          key={toast.id}
          className={cn(
            toastVariants({ variant: toast.variant }),
            toast.dismissible === false ? '' : 'pr-12',
            placement === 'inline' ? 'shrink-0' : '',
          )}
          duration={toast.persistent ? PERSISTENT_DURATION_MS : toast.duration}
          defaultOpen
          onOpenChange={(nextOpen) => {
            if (!nextOpen) {
              setTimeout(() => removeToast(toast.id), EXIT_DURATION);
            }
          }}
        >
          <div className={cn(
            'flex w-full',
            toast.variant === 'snapshot' ? 'flex-row items-center gap-3' : 'flex-col gap-1',
          )}>
            {toast.title && <div className={cn(toastTitleVariants, toast.variant === 'snapshot' ? 'min-w-0 flex-1 text-sm font-medium leading-5 text-white' : '')}>{toast.title}</div>}
            {toast.description && <div className={cn(
              toastDescriptionVariants,
              toast.variant === 'snapshot' ? 'text-white/75' : '',
              !toast.variant || toast.variant === 'default' ? 'text-inherit' : '',
            )}>{toast.description}</div>}
            {toast.action}
          </div>
          {toast.dismissible === false ? null : (
            <ToastPrimitive.Close aria-label="Dismiss notification" className={cn(
              'absolute right-3 top-3 inline-flex h-8 w-8 items-center justify-center rounded-full bg-transparent transition hover:bg-muted/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              !toast.variant || toast.variant === 'default' ? 'text-inherit' : 'text-muted-foreground',
            )}>
              <X className="h-4 w-4" />
            </ToastPrimitive.Close>
          )}
        </ToastPrimitive.Root>
      ))}
      <ToastPrimitive.Viewport
        data-testid={placement === 'inline' ? 'meeting-notifications' : 'overlay-notifications'}
        style={placement === 'inline' ? { paddingTop: 'max(0.5rem, env(safe-area-inset-top))' } : undefined}
        className={placement === 'inline'
          ? 'm-0 flex max-h-[min(30dvh,16rem)] w-full flex-col items-end gap-2 overflow-y-auto overscroll-contain p-2 outline-none'
          : 'fixed left-1/2 bottom-[calc(env(safe-area-inset-bottom)+5.5rem)] z-[1000] flex w-[min(calc(100vw-1rem),420px)] -translate-x-1/2 flex-col gap-3 outline-none sm:bottom-6 sm:right-6 sm:left-auto sm:w-auto sm:translate-x-0'}
      />
    </ToastPrimitive.Provider>
    </div>
  );
};
